import { assertSupabaseConfigured, supabase } from "../lib/supabase";
import type {
  CreateMealPlanInput,
  MealPlan,
  MealPlanDay,
  MealPlanItem,
  PlanAssignmentInput,
  PlanStatus,
} from "../types/coaching";

const MEAL_SELECT = `
  *,
  meal_plan_days (
    *,
    meal_plan_items (*)
  )
`;

function sortMealPlan(plan: MealPlan): MealPlan {
  return {
    ...plan,
    meal_plan_days: [...(plan.meal_plan_days ?? [])]
      .sort((left, right) => left.day_index - right.day_index)
      .map((day) => ({
        ...day,
        meal_plan_items: [...(day.meal_plan_items ?? [])].sort(
          (left, right) => left.sort_order - right.sort_order,
        ),
      })),
  };
}

function parseOptionalNumber(value?: string) {
  if (!value?.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export const mealPlanService = {
  async listMealPlansForTrainer(
    tenantId: string,
    status: PlanStatus | "all" = "all",
  ): Promise<MealPlan[]> {
    assertSupabaseConfigured();

    let query = (supabase as any)
      .from("meal_plans")
      .select(MEAL_SELECT)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false });

    if (status !== "all") query = query.eq("status", status);

    const { data, error } = await query;
    if (error) throw error;
    return ((data ?? []) as MealPlan[]).map(sortMealPlan);
  },

  async listMealTemplatesForTrainer(tenantId: string): Promise<MealPlan[]> {
    assertSupabaseConfigured();

    const { data, error } = await (supabase as any)
      .from("meal_plans")
      .select(MEAL_SELECT)
      .eq("tenant_id", tenantId)
      .eq("is_template", true)
      .neq("status", "archived")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return ((data ?? []) as MealPlan[]).map(sortMealPlan);
  },

  async createMealPlan(input: CreateMealPlanInput): Promise<MealPlan> {
    assertSupabaseConfigured();

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data: plan, error: planError } = await (supabase as any)
      .from("meal_plans")
      .insert({
        tenant_id: input.tenantId,
        created_by: user?.id ?? null,
        title: input.title.trim(),
        description: input.description?.trim() || null,
        is_template: input.isTemplate ?? true,
        status: "draft",
        target_calories: parseOptionalNumber(input.calories),
        target_protein_grams: parseOptionalNumber(input.proteinGrams),
        target_carbs_grams: parseOptionalNumber(input.carbsGrams),
        target_fat_grams: parseOptionalNumber(input.fatGrams),
      })
      .select("*")
      .single();

    if (planError) throw planError;

    const { data: day, error: dayError } = await (supabase as any)
      .from("meal_plan_days")
      .insert({
        plan_id: plan.id,
        day_index: 1,
        title: input.dayTitle?.trim() || "Day 1",
        notes: input.notes?.trim() || null,
      })
      .select("*")
      .single();

    if (dayError) throw dayError;

    if (input.mealName?.trim() && input.foodDetails?.trim()) {
      const { error: itemError } = await (supabase as any)
        .from("meal_plan_items")
        .insert({
          day_id: day.id,
          meal_name: input.mealName.trim(),
          food_details: input.foodDetails.trim(),
          calories: parseOptionalNumber(input.calories),
          protein_grams: parseOptionalNumber(input.proteinGrams),
          carbs_grams: parseOptionalNumber(input.carbsGrams),
          fat_grams: parseOptionalNumber(input.fatGrams),
          notes: input.notes?.trim() || null,
          sort_order: 1,
        });

      if (itemError) throw itemError;
    }

    const created = await this.getMealPlan(plan.id);
    if (!created) throw new Error("Meal plan was created but could not be loaded.");
    return created;
  },

  async getMealPlan(planId: string): Promise<MealPlan | null> {
    assertSupabaseConfigured();

    const { data, error } = await (supabase as any)
      .from("meal_plans")
      .select(MEAL_SELECT)
      .eq("id", planId)
      .maybeSingle();

    if (error) throw error;
    return data ? sortMealPlan(data as MealPlan) : null;
  },

  async updateMealPlan(
    planId: string,
    input: Partial<Pick<MealPlan, "title" | "description" | "status">>,
  ): Promise<MealPlan> {
    assertSupabaseConfigured();

    const { data, error } = await (supabase as any)
      .from("meal_plans")
      .update(input)
      .eq("id", planId)
      .select(MEAL_SELECT)
      .single();

    if (error) throw error;
    return sortMealPlan(data as MealPlan);
  },

  async assignMealPlanToClient(
    planId: string,
    clientId: string,
    options: PlanAssignmentInput = {},
  ): Promise<MealPlan> {
    assertSupabaseConfigured();

    const source = await this.getMealPlan(planId);
    if (!source) throw new Error("Meal plan not found.");

    if (source.is_template || source.client_id !== clientId) {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: plan, error: planError } = await (supabase as any)
        .from("meal_plans")
        .insert({
          tenant_id: source.tenant_id,
          client_id: clientId,
          created_by: user?.id ?? source.created_by,
          title: source.title,
          description: source.description,
          status: "active",
          is_template: false,
          assigned_at: new Date().toISOString(),
          starts_on: options.startsOn || null,
          ends_on: options.endsOn || null,
          target_calories: source.target_calories,
          target_protein_grams: source.target_protein_grams,
          target_carbs_grams: source.target_carbs_grams,
          target_fat_grams: source.target_fat_grams,
        })
        .select("*")
        .single();

      if (planError) throw planError;

      for (const sourceDay of source.meal_plan_days ?? []) {
        const { data: day, error: dayError } = await (supabase as any)
          .from("meal_plan_days")
          .insert({
            plan_id: plan.id,
            day_index: sourceDay.day_index,
            title: sourceDay.title,
            notes: sourceDay.notes,
          })
          .select("*")
          .single();

        if (dayError) throw dayError;

        const items = (sourceDay.meal_plan_items ?? []).map((item) => ({
          day_id: day.id,
          meal_name: item.meal_name,
          food_details: item.food_details,
          calories: item.calories,
          protein_grams: item.protein_grams,
          carbs_grams: item.carbs_grams,
          fat_grams: item.fat_grams,
          alternatives: item.alternatives,
          notes: item.notes,
          sort_order: item.sort_order,
        }));

        if (items.length) {
          const { error: itemError } = await (supabase as any)
            .from("meal_plan_items")
            .insert(items);

          if (itemError) throw itemError;
        }
      }

      const assigned = await this.getMealPlan(plan.id);
      if (!assigned) throw new Error("Assigned meal plan could not be loaded.");
      return assigned;
    }

    const { data, error } = await (supabase as any)
      .from("meal_plans")
      .update({
        client_id: clientId,
        status: "active",
        is_template: false,
        assigned_at: new Date().toISOString(),
        starts_on: options.startsOn || null,
        ends_on: options.endsOn || null,
      })
      .eq("id", planId)
      .select(MEAL_SELECT)
      .single();

    if (error) throw error;
    return sortMealPlan(data as MealPlan);
  },

  async archiveMealPlan(planId: string): Promise<void> {
    assertSupabaseConfigured();

    const { error } = await (supabase as any)
      .from("meal_plans")
      .update({ status: "archived" })
      .eq("id", planId);

    if (error) throw error;
  },

  async listAssignedMealPlansForClient(clientId: string): Promise<MealPlan[]> {
    assertSupabaseConfigured();

    const { data, error } = await (supabase as any)
      .from("meal_plans")
      .select(MEAL_SELECT)
      .eq("client_id", clientId)
      .eq("status", "active")
      .order("assigned_at", { ascending: false });

    if (error) throw error;
    return ((data ?? []) as MealPlan[]).map(sortMealPlan);
  },
};

export type { MealPlan, MealPlanDay, MealPlanItem };
